<template>
  <div>
    <DropdownMenu>
      <DropdownMenuTrigger as-child>
        <button
          class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-xs tw-text-secondary-500 hover:tw-text-white tw-bg-white tw-border tw-border-solid tw-border-secondary-600 tw-rounded-md hover:tw-bg-secondary-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-secondary-500 focus:tw-border-secondary-500"
          type="button"
        >
          <div class="tw-flex tw-items-center tw-gap-2">
            <span class="tw-text-xs tw-font-light">
              {{ t('common.actions.bulk_actions') }}
            </span>
            <DotsVerticalIcon class="tw-w-4 tw-h-4" />
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        class="tw-w-56 !tw-border-none !tw-shadow-[0px_0px_19px_0px_#f5f5f5]"
        side="bottom"
      >
        <DropdownMenuItem
          v-for="action in actions"
          :key="action.label"
          class="tw-cursor-pointer hover:!tw-bg-gray-100 tw-transition-colors tw-group"
          @click="action.onClick"
        >
          <div class="tw-flex tw-items-center tw-gap-2">
            <component
              :is="action.icon"
              class="tw-w-4 tw-h-4"
              :class="action.iconClass"
              v-if="action.icon"
            />
            <span>{{ action.label }}</span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    <ConfirmationModal
      v-model="showActivateConfirmationModal"
      :title="t('users.titles.attention').toUpperCase()"
      :message="
        t('users.titles.activate_confirmation', {
          usersCount: props.selectedUsers.length,
        })
      "
      type="info"
      :confirm-text="t('common.forms.confirm')"
      :cancel-text="t('common.forms.cancel')"
      @confirm="onActivateConfirmation"
      @cancel="closeActivateConfirmationModal"
    />
    <ConfirmationModal
      v-model="showDeactivateConfirmationModal"
      :title="t('users.titles.deactivate_selected')"
      :message="
        t('users.titles.deactivate_confirmation', {
          usersCount: props.selectedUsers.length,
        })
      "
      type="danger"
      :confirm-text="t('common.forms.confirm')"
      :cancel-text="t('common.forms.cancel')"
      @confirm="onDeactivateConfirmation"
      @cancel="closeDeactivateConfirmationModal"
    />

    <GenericModal
      v-model="showDeactivateWarningModal"
      :title="t('users.titles.attention').toUpperCase()"
    >
      <template #body>
        <div class="tw-flex tw-flex-col tw-gap-4">
          <ul class="tw-list-disc tw-pl-5">
            <li
              class="tw-text-sm tw-text-gray-500"
              v-for="warning in deactivateWarnings"
              :key="warning"
            >
              {{ warning }}
            </li>
          </ul>
        </div>
      </template>
    </GenericModal>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { UserInList } from '@/_helpers/interfaces';
import { ConfirmationModal, GenericModal } from '@/components/shared';
import { useUserStore } from '@/stores/user.store';
import { useApiUtils } from '@/_helpers/composables/api-utils.composable';

import DotsVerticalIcon from '@/assets/icons/DotsVertical.svg';
import CheckMarkSingle from '@/assets/icons/CheckMarkSingle.svg';
import TrashIcon from '@/assets/icons/Trash.svg';

const { handleApiError } = useApiUtils();
const { changeBulkUsersActivityStatus } = useUserStore();

const props = defineProps<{
  selectedUsers: UserInList[];
}>();

const emit = defineEmits(['reloadUsersTable']);

const showActivateConfirmationModal = ref(false);
const showDeactivateConfirmationModal = ref(false);
const showDeactivateWarningModal = ref(false);
const deactivateWarnings = ref<string[]>([]);

const handleActivate = () => {
  showActivateConfirmationModal.value = true;
};

const handleDeactivate = () => {
  showDeactivateConfirmationModal.value = true;
};

const onActivateConfirmation = async () => {
  await handleApiError(async () => {
    return await changeBulkUsersActivityStatus(
      'activate',
      props.selectedUsers.map(user => user.id)
    );
  }, t('users.titles.bulk_activation_success'));

  closeActivateConfirmationModal();
  emit('reloadUsersTable');
};

const onDeactivateConfirmation = async () => {
  const bulkDeactivationResults = await handleApiError(async () => {
    return await changeBulkUsersActivityStatus(
      'deactivate',
      props.selectedUsers.map(user => user.id)
    );
  }, t('users.titles.bulk_deactivation_success'));

  closeDeactivateConfirmationModal();
  if (bulkDeactivationResults.warnings?.length) {
    deactivateWarnings.value = bulkDeactivationResults.warnings;
    showDeactivateWarningModal.value = true;
  }
  emit('reloadUsersTable');
};

const closeActivateConfirmationModal = () => {
  showActivateConfirmationModal.value = false;
};

const closeDeactivateConfirmationModal = () => {
  showDeactivateConfirmationModal.value = false;
};

const { t } = useI18n();

const actions = ref<any[]>([
  {
    label: t('users.titles.activate_selected'),
    icon: CheckMarkSingle,
    iconClass: 'tw-text-green-500',
    onClick: handleActivate,
  },
  {
    label: t('users.forms.deactivate_selected'),
    icon: TrashIcon,
    iconClass: 'tw-text-red-500',
    onClick: handleDeactivate,
  },
]);
</script>
